// src/components/Student/Dashboard.jsx
import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  AppBar,
  Toolbar,
  Avatar,
  IconButton,
  Menu,
  MenuItem,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Divider,
  useTheme,
  useMediaQuery,
  Badge // Added Badge import
} from '@mui/material';
import {
  Logout as LogoutIcon,
  Person as PersonIcon,
  Language as LanguageIcon,
  School as SchoolIcon,
  Grade as GradeIcon,
  QueryStats as StatsIcon,
  Report as ReportIcon,
  Dashboard as DashboardIcon,
  Menu as MenuIcon,
  Assignment as AssignmentIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
  AccessTime as TimeIcon,
  Description as DescriptionIcon,
  EventNote as EventNoteIcon,
  EmojiEvents as ExamResultsIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { studentAPI } from '../../services/api';

// Import page components
import DashboardHome from './DashboardHome';
import StudentProfile from './StudentProfile';
import StudentGrades from './StudentGrades';
import StudentDiplomaProgress from './StudentDiplomaProgress';
import PedagogicalSituation from './PedagogicalSituation';
import StudentRequests from './StudentRequests';
import AdministrativeSituation from './AdministrativeSituation';
import StudentSchedule from './StudentSchedule';
import OfficialDocuments from './OfficialDocuments';
import ExamConvocation from './ExamConvocation';
import CurrentSessionGrades from './CurrentSessionGrades';
import NotificationBell from './NotificationBell';
import LegalFooter from '../common/LegalFooter';

const DRAWER_WIDTH = 280;

const Dashboard = () => {
  const { user, logout } = useAuth();
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [currentPage, setCurrentPage] = useState('dashboard');
  const [languageMenu, setLanguageMenu] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(!isMobile);
  const [menuConfig, setMenuConfig] = useState(null);

  const handleLogout = () => {
    logout();
  };

  // Load admin-configured menu visibility on mount
  React.useEffect(() => {
    studentAPI.getMenuConfig()
      .then(cfg => {
        setMenuConfig(cfg);
        // If student is on a page that got disabled, redirect to dashboard
        if (currentPage !== 'dashboard' && cfg[currentPage] === false) {
          setCurrentPage('dashboard');
        }
      })
      .catch(() => {});
  }, []);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    if (isMobile) {
      setDrawerOpen(false);
    }
  };

  const handleLanguageMenuOpen = (event) => {
    setLanguageMenu(event.currentTarget);
  };

  const handleLanguageMenuClose = () => {
    setLanguageMenu(null);
  };

  const changeLanguage = (lang) => {
    localStorage.setItem('language', lang);
    window.location.reload();
  };

  const toggleDrawer = () => {
    setDrawerOpen(!drawerOpen);
  };

  const allNavigationItems = [
    { id: 'dashboard',      label: t('dashboard'),              icon: <DashboardIcon />,          color: '#3498db', alwaysOn: true },
    { id: 'profile',        label: t('Profile'),                icon: <PersonIcon />,             color: '#2ecc71' },
    { id: 'administrative', label: t('situationA'),             icon: <AdminPanelSettingsIcon />, color: '#8e44ad' },
    { id: 'pedagogical',    label: t('pedagogicalSituation'),   icon: <AssignmentIcon />,         color: '#f39c12' },
    { id: 'grades',         label: t('gradesAndResults'),       icon: <GradeIcon />,              color: '#e74c3c' },
    { id: 'stats',          label: t('diplomaProgress'),        icon: <StatsIcon />,              color: '#9b59b6' },
    { id: 'schedule',       label: t('timeSchedule'),           icon: <TimeIcon />,               color: '#12f3ce' },
    { id: 'documents',      label: t('officialDocuments'),      icon: <DescriptionIcon />,        color: '#0891b2' },
    { id: 'convocation',    label: t('examConvocation'),        icon: <EventNoteIcon />,          color: '#7c3aed' },
    { id: 'exam-results',   label: t('examResults'),            icon: <ExamResultsIcon />,        color: '#1565c0' },
    { id: 'requests',       label: t('requestsAndComplaints'),  icon: <ReportIcon />,             color: '#c62b3dff' },
  ];

  // Wait for menuConfig to load before filtering — prevents flash on first login
  const navigationItems = allNavigationItems.filter(item => {
    if (item.alwaysOn) return true;
    if (menuConfig === null) return false;           // still loading — hide until ready
    if (menuConfig[item.id] === false) return false;
    const diplomeFilter = menuConfig[`${item.id}.diplomes`];
    if (diplomeFilter && diplomeFilter.trim()) {
      const userDiplome = (user?.diplome || '').toUpperCase();
      if (diplomeFilter.startsWith('EXCEPT:')) {
        const excluded = diplomeFilter.slice(7).split(',').map(d => d.trim().toUpperCase()).filter(Boolean);
        return !excluded.some(p => userDiplome.startsWith(p));
      }
      const allowed = diplomeFilter.split(',').map(d => d.trim().toUpperCase()).filter(Boolean);
      return allowed.some(prefix => userDiplome.startsWith(prefix));
    }
    return true;
  });

  const renderPageContent = () => {
    switch (currentPage) {
      case 'dashboard':
        return <DashboardHome onNavigate={handlePageChange} menuConfig={menuConfig || {}} />;
      case 'profile':
        return <StudentProfile />;
      case 'administrative':
        return <AdministrativeSituation />;
      case 'pedagogical':
        return <PedagogicalSituation />;
      case 'grades':
        return <StudentGrades />;
      case 'stats':
        return <StudentDiplomaProgress />;
      case 'schedule':
        return <StudentSchedule />;
      case 'documents':
        return <OfficialDocuments />;
      case 'convocation':
        return <ExamConvocation menuConfig={menuConfig || {}} />;
      case 'exam-results':
        return <CurrentSessionGrades />;
      case 'requests':
        return <StudentRequests />;
      default:
        return <DashboardHome onNavigate={handlePageChange} />;
    }
  };

  const drawer = (
    <Box sx={{ height: '100%', bgcolor: '#f8f9fa', display: 'flex', flexDirection: 'column' }}>
      {/* Drawer Header */}
      <Box 
        sx={{ 
          p: 3, 
          background: 'linear-gradient(135deg, #2c3e50 0%, #3498db 100%)',
          color: 'white',
          textAlign: 'center'
        }}
      >
      </Box>

      {/* User Info */}
      {user && (
        <Box sx={{ p: 3, textAlign: 'center', bgcolor: 'white', mx: 2, mt: 2, borderRadius: 3 }}>
          <Avatar
            src={studentAPI.getStudentPhotoUrl(user.cod_etu)}
            sx={{
              width: 60,
              height: 60,
              mx: 'auto',
              mb: 2,
              background: 'linear-gradient(135deg, #3498db 0%, #2980b9 100%)',
              fontSize: '1.5rem',
              fontWeight: 'bold'
            }}
          >
            {user.nom_complet?.charAt(0) || user.cod_etu?.charAt(0) || 'S'}
          </Avatar>
          <Typography variant="h6" fontWeight="600" gutterBottom>
            {user.nom_complet || user.cod_etu}
          </Typography>
          {user.nom_arabe && (
            <Typography 
              variant="body2" 
              color="text.secondary"
              sx={{ 
                direction: 'rtl',
                fontFamily: 'Arabic UI Text, Arial',
                mb: 1
              }}
            >
              {user.nom_arabe}
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary">
            {user.etape}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            {user.cod_etu}
          </Typography>
        </Box>
      )}

      {/* Navigation */}
      <Box sx={{ mt: 2, flexGrow: 1, overflowY: 'auto' }}>
        <List sx={{ px: 2 }}>
          {navigationItems.map((item) => (
            <ListItem key={item.id} disablePadding sx={{ mb: 1 }}>
              <ListItemButton
                onClick={() => !item.disabled && handlePageChange(item.id)} // Prevent click if disabled
                selected={currentPage === item.id}
                disabled={item.disabled} // Visually disable the button
                sx={{
                  borderRadius: 3,
                  '&.Mui-selected': {
                    background: `linear-gradient(135deg, ${item.color} 0%, ${item.color}CC 100%)`,
                    color: 'white',
                    '&:hover': {
                      background: `linear-gradient(135deg, ${item.color}CC 0%, ${item.color} 100%)`,
                    }
                  },
                  '&:hover': {
                    background: `${item.color}15`,
                  },
                  '&.Mui-disabled': {
                    opacity: 0.6 // Ensure disabled items are visually distinct
                  }
                }}
              >
                <ListItemIcon 
                  sx={{ 
                    color: currentPage === item.id ? 'white' : item.color,
                    minWidth: 40 
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText 
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                      <Typography 
                        variant="body2" 
                        sx={{ fontWeight: currentPage === item.id ? 600 : 500 }}
                      >
                        {item.label}
                      </Typography>
                      {item.underDevelopment && ( // Add badge for development items
                        <Badge 
                          badgeContent="قيد التطوير" 
                          color="warning" 
                          sx={{ 
                            '& .MuiBadge-badge': { 
                              fontSize: '0.65rem', 
                              height: '20px', 
                              minWidth: '65px',
                              position: 'static',
                              transform: 'none',
                              fontFamily: 'Arabic UI Text, Arial',
                              fontWeight: 'bold'
                            } 
                          }} 
                        />
                      )}
                    </Box>
                  }
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Box>

      {/* Footer */}
      <Box sx={{ mt: 'auto', p: 2 }}>
        <Divider sx={{ mb: 2 }} />
        <Button
          fullWidth
          variant="outlined"
          color="error"
          onClick={handleLogout}
          sx={{ borderRadius: 3 }}
        >
          {t('logout')}
        </Button>
      </Box>
    </Box>
  );

  return (
    <Box dir={i18n.language === 'ar' ? 'rtl' : 'ltr'} sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Top AppBar */}
      <AppBar
        position="fixed"
        sx={{
          zIndex: theme.zIndex.drawer + 1,
          background: 'linear-gradient(135deg, #2c3e50 0%, #3498db 100%)'
        }}
      >
        <Toolbar>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton
              color="inherit"
              edge="start"
              onClick={toggleDrawer}
              sx={{ mr: 2, display: { md: 'none' } }}
            >
              <MenuIcon />
            </IconButton>
            <SchoolIcon sx={{ mr: 2, display: { xs: 'none', md: 'block' } }} />
            <Typography variant="h6" component="div">
              {t('studentSystem')}
            </Typography>
          </Box>

          <Box sx={{ flexGrow: 1 }} />

          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <NotificationBell />
            <IconButton
              color="inherit"
              onClick={handleLanguageMenuOpen}
              sx={{ mr: 1 }}
            >
              <LanguageIcon />
            </IconButton>
            <Menu
              anchorEl={languageMenu}
              open={Boolean(languageMenu)}
              onClose={handleLanguageMenuClose}
            >
              <MenuItem onClick={() => changeLanguage('ar')}>
                العربية
              </MenuItem>
              <MenuItem onClick={() => changeLanguage('fr')}>
                Français
              </MenuItem>
            </Menu>

            {user && (
              <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', ml: 2 }}>
                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 32, height: 32 }}>
                  {user.nom_complet?.charAt(0) || 'S'}
                </Avatar>
              </Box>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      {/* Sidebar Drawer */}
      <Drawer
        variant={isMobile ? "temporary" : "permanent"}
        anchor="left"
        open={drawerOpen}
        onClose={toggleDrawer}
        sx={{
          width: DRAWER_WIDTH,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            boxSizing: 'border-box',
            border: 'none',
            boxShadow: '2px 0 8px rgba(0,0,0,0.1)',
            position: 'fixed',
            left: 0,
            right: 'auto'
          },
        }}
      >
        {drawer}
      </Drawer>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          position: 'fixed',
          top: '64px',
          left: { xs: 0, md: `${DRAWER_WIDTH}px` },
          right: 0,
          bottom: 0,
          bgcolor: '#f5f5f5',
          overflow: 'auto',
          '&[dir="rtl"]': {
            left: { xs: 0, md: `${DRAWER_WIDTH}px` },
            right: 0
          }
        }}
      >
        <Container maxWidth="xl" sx={{ py: 3, width: '100%', minHeight: '100%', display: 'flex', flexDirection: 'column' }}>
          <div
            dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}
            style={{ direction: i18n.language === 'ar' ? 'rtl' : 'ltr', width: '100%', flexGrow: 1 }}
          >
            {renderPageContent()}
          </div>

          {/* Footer */}
          <Box
            component="footer"
            sx={{
              mt: 4,
              py: 2,
              borderTop: '1px solid #e2e8f0',
              textAlign: 'center',
            }}
          >
            <Typography variant="caption" color="text.disabled" display="block">
              © {new Date().getFullYear()} {i18n.language === 'ar'
                ? 'كلية العلوم القانونية والسياسية — سطات'
                : 'Faculté des Sciences Juridiques et Politiques — Settat'}
            </Typography>
            <Typography variant="caption" color="text.disabled" display="block" sx={{ mt: 0.3 }}>
              {i18n.language === 'ar' ? 'جميع الحقوق محفوظة' : 'Tous droits réservés'}
              {' — '}
              <Box component="span" sx={{ color: '#e67e22', fontWeight: 700, fontSize: 'inherit' }}>
                Version Bêta
              </Box>
            </Typography>
            <LegalFooter />
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default Dashboard;